import myAxios from './axiositeachWrapper';
import Config from '../_config/config';

const BASE_PATH = Config.nodeSiteUrl + 'v1/academic/template-scheduling';

export interface SlotCandidate {
  id?: number | null;
  date: string; // 'YYYY-MM-DD'
  roomId: number;
  shiftId?: number | null;
  courseId: number;
  timecraftInputId?: number; // cohort identity — same courseId can have multiple cohorts
  instructorId: number;
  startTime: string; // 'HH:MM'
  endTime: string; // 'HH:MM'
  groupSize: number;
  name?: string;
  remarks?: string;
  templateId?: number; // Required for template slot creation
  lessonPlanStepId?: number | null;
  splitIndex?: number;
}

export interface ConflictCheckResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface InstructorAvailabilityException {
  day: string;
  status: string;
  time: string;
}

export interface InstructorAvailability {
  default: string;
  exceptions: InstructorAvailabilityException[];
}

export interface InstructorAssignment {
  instructorId: number;
  code: string;
  name: string;
  role: 'primary' | 'secondary';
  remarks?: string | null;
  availability?: InstructorAvailability | null;
}

export interface TemplateDataResponse {
  template: {
    id: number;
    name: string;
    startDate: string;
    endDate: string;
    courseId?: number | null;
    programId?: number | null;
  };
  inputs: Array<{
    id: number;
    moduleCode: string;
    moduleName: string;
    instructorId: number | null;
    instructorName: string;
    instructors?: InstructorAssignment[] | null;
    studentGroup: string;
    sessions: number;
    enrollments: number;
    plannedCapacity?: number | null;
    startDate: string;
    endDate: string;
    courseId: number;
  }>;
  rooms: Array<{
    id: number;
    name: string;
    capacity: number;
    shiftId: number;
    shiftName: string;
    shiftCode: string;
    shiftStartTime: string;
    shiftEndTime: string;
    resourceId: string;
  }>;
  instructors: Array<{
    id: number;
    name: string;
    code: string;
    email: string;
    activeStatus: number;
  }>;
}

export interface ActivitySlot {
  id: number;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  roomId: number;
  shiftId: number;
  instructorId: number;
  courseId?: number | null;
  topicMasterId?: number | null;
  remarks: string;
  activeFlag: number;
}

export interface ValidationResult {
  valid: boolean;
  slotCount: number;
  warnings: string[];
  errors: string[];
}

export interface TemplateApplicationResult {
  slotsCreated: number;
  batchMappingsCreated: number;
}

export interface RoomAvailabilityStatus {
  roomId: number;
  roomName: string;
  shiftId: number;
  shiftName: string;
  resourceId: number;
  capacity: number;
  status: 'available' | 'warning' | 'conflict' | 'not-in-pool';
  conflicts: string[];
  warnings: string[];
}

export interface RoomAvailabilityCheck {
  date: string;
  startTime: string;
  endTime: string;
  courseId: number;
  instructorId: number;
  groupSize: number;
}

class TemplateSchedulingService {
  /**
   * Get all data needed for template scheduling board
   */
  static async getTemplateData(templateId: number) {
    return myAxios.get(`${BASE_PATH}/template/${templateId}/data`);
  }

  /**
   * Get existing template slots for a specific date
   */
  static async getTemplateSlots(templateId: number, date: string) {
    const params = new URLSearchParams({ templateId: templateId.toString(), date });
    return myAxios.get(`${BASE_PATH}/template/slots?${params.toString()}`);
  }

  /**
   * Get existing template slots for a date range (week/month)
   */
  static async getTemplateSlotsForWeek(templateId: number, startDate: string, endDate: string) {
    const params = new URLSearchParams({
      templateId: templateId.toString(),
      startDate,
      endDate,
    });
    return myAxios.get(`${BASE_PATH}/template/slots?${params.toString()}`);
  }

  /**
   * Validate a template slot candidate without creating it
   */
  static async validateTemplateSlot(candidate: SlotCandidate) {
    return myAxios.post(`${BASE_PATH}/template/slots/validate`, candidate);
  }

  /**
   * Create a new template slot
   */
  static async createTemplateSlot(candidate: SlotCandidate) {
    return myAxios.post(`${BASE_PATH}/template/slots`, candidate);
  }

  /**
   * Update an existing template slot
   */
  static async updateTemplateSlot(slotId: number, candidate: SlotCandidate) {
    return myAxios.put(`${BASE_PATH}/template/slots/${slotId}`, candidate);
  }

  /**
   * Delete a template slot
   */
  static async deleteTemplateSlot(slotId: number) {
    return myAxios.delete(`${BASE_PATH}/template/slots/${slotId}`);
  }

  /**
   * Bulk operations on template slots. Match the regular service names so
   * WeekGrid can dispatch via `service[method]` without per-mode branching.
   */
  static async bulkDeleteSlots(slotIds: number[]) {
    return myAxios.post(`${BASE_PATH}/template/slots/bulk-delete`, { slotIds });
  }
  static async bulkChangeRoom(slotIds: number[], roomId: number, _shiftId?: number | null) {
    return myAxios.post(`${BASE_PATH}/template/slots/bulk-change-room`, { slotIds, roomId });
  }
  static async bulkChangeInstructor(slotIds: number[], instructorId: number) {
    return myAxios.post(`${BASE_PATH}/template/slots/bulk-change-instructor`, { slotIds, instructorId });
  }

  /**
   * Suggest the next available slot for template
   */
  static async suggestNextTemplateSlot(
    templateId: number,
    params: {
      instructorId: number;
      courseId?: number;
      preferredDate?: string;
      roomId?: number;
      shiftId?: number;
      groupSize?: number;
      timeSlotPresets?: Array<{ startTime: string; endTime: string }>;
    }
  ) {
    return myAxios.post(`${BASE_PATH}/template/${templateId}/suggest-next-slot`, params);
  }

  /**
   * Check room availability for a given time slot
   */
  static async checkRoomAvailability(templateId: number, check: RoomAvailabilityCheck) {
    return myAxios.post(`${BASE_PATH}/template/${templateId}/check-availability`, check);
  }

  /**
   * Get holidays within optional date range
   */
  static async getHolidays(startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return myAxios.get(`${BASE_PATH}/template/holidays?${params.toString()}`);
  }

  /**
   * Get macro planning constraint summary for a date
   */
  static async getConstraintSummary(templateId: number, date: string) {
    const params = new URLSearchParams({
      templateId: templateId.toString(),
      date,
    });
    return myAxios.get(`${BASE_PATH}/template/constraint-summary?${params.toString()}`);
  }

  /**
   * Get lesson plan steps for all modules on a template (via source board)
   */
  static async getLessonPlanSteps(templateId: number) {
    return myAxios.get(`${BASE_PATH}/template/${templateId}/lesson-plan-steps`);
  }

  /**
   * Get available boards for template application
   */
  static async getBoards() {
    // Uses the TimeCraft boards API endpoint
    return myAxios.get(Config.nodeSiteUrl + 'v1/academic/timecraft/boards');
  }

  /**
   * Validate template application to a board (dry-run)
   */
  static async validateTemplateApplication(templateId: number, boardId: number, startDate: string) {
    return myAxios.post(`${BASE_PATH}/template/${templateId}/validate-application`, {
      boardId,
      startDate,
    });
  }

  /**
   * Apply template to a board (creates regular slots from template)
   */
  static async applyTemplateToBoard(templateId: number, boardId: number, startDate: string) {
    return myAxios.post(`${BASE_PATH}/template/${templateId}/apply-to-board`, {
      boardId,
      startDate,
    });
  }

  /**
   * Copy a template with all its slots
   */
  static async copyTemplate(templateId: number, name?: string) {
    return myAxios.post(`${BASE_PATH}/template/${templateId}/copy`, { name });
  }

  /**
   * Auto-schedule all remaining unscheduled steps/splits
   */
  static async autoSchedule(
    templateId: number,
    params?: {
      timeSlotPresets?: Array<{ startTime: string; endTime: string }>;
      maxSlots?: number;
    }
  ) {
    return myAxios.post(`${BASE_PATH}/template/${templateId}/auto-schedule`, params || {});
  }
}

export default TemplateSchedulingService;
